import '.././pages.css';
import { useLocation } from 'react-router-dom';
import DateRangePicker, { computeRangeForPreset } from '../../components/Filters/DateRangePicker';
import FilterPanel from '../../components/Filters/FilterPanel';
import DailyOverview from '../../components/Cards/DailyOverviewChart';
import LastSyncDisplay from '../../components/LastSyncDisplay';
import React, { useState } from 'react';
import TestResultsTable from '../../components/BrokenTestsTable/BrokenTestsTable';
import useFiltersData from '../../hooks/useFiltersData';
import useLastSync from '../../hooks/useLastSync';
import usePackages from '../../hooks/usePackages';
import EmptyCard from '../../components/Cards/EmptyCard';
import { formatLocalDate } from '../../utils/dateFormat';

function Filters({ dateRange, datePreset, setDateRange, selectedEnvironment, selectedPackage, onFilterApply, lastSync, lastSyncLoading, availablePackages, packagesLoading }) {
  const filterFields = [
    {
      key: 'env',
      label: 'Environment',
      options: ['All Environments', 'Local', 'Staging', 'Production'],
      defaultValue: 'All Environments',
    },
    {
      key: 'pkg',
      label: 'Package',
      options: packagesLoading ? [selectedPackage] : (availablePackages || [selectedPackage]),
      defaultValue: 'tag',
      disabled: packagesLoading,
    },
  ];

  return (
    <div className="filters-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <DateRangePicker
          value={dateRange}
          preset={datePreset}
          onChange={setDateRange}
          maxDate={new Date()}
          enabledPresets={['today', 'yesterday', '7d', '14d']}
        />
        <FilterPanel
          fields={filterFields}
          values={{ env: selectedEnvironment, pkg: selectedPackage }}
          onApply={onFilterApply}
        />
      </div>
      <LastSyncDisplay lastSync={lastSync} loading={lastSyncLoading} />
    </div>
  );
}

function TestMonitoring() {
  const [filters, setFilters] = useState(() => {
    const initial = computeRangeForPreset('today');
    return {
      dateRange: initial,
      datePreset: 'today',
      selectedEnvironment: 'All Environments',
      selectedPackage: 'tag',
      selectedFramework: 'All Frameworks',
      selectedUnit: 'All Units',
    };
  });

  const location = useLocation();
  const setDateRange = ({ startDate, endDate, preset }) =>
    setFilters(prev => ({ ...prev, dateRange: { startDate, endDate }, datePreset: preset }));
  const handleFilterApply = ({ env, pkg }) =>
    setFilters(prev => ({ ...prev, selectedEnvironment: env, selectedPackage: pkg }));

  const startDate = formatLocalDate(filters.dateRange.startDate);
  const endDate = formatLocalDate(filters.dateRange.endDate);

  const { data, loading, error } = useFiltersData(
    startDate,
    endDate,
    filters.selectedEnvironment,
    filters.selectedPackage,
    filters.selectedFramework,
    filters.selectedUnit
  );

  const { data: lastSync, loading: lastSyncLoading } = useLastSync(
    filters.selectedEnvironment,
    filters.selectedPackage
  );

  const { data: availablePackages, loading: packagesLoading } = usePackages("tag");

  return (
    <div className="application-container table-owns-scroll">
      {location.pathname === '/tag/tests-monitoring' && (
        <>
          <Filters
            dateRange={filters.dateRange}
            datePreset={filters.datePreset}
            setDateRange={setDateRange}
            selectedEnvironment={filters.selectedEnvironment}
            selectedPackage={filters.selectedPackage}
            onFilterApply={handleFilterApply}
            lastSync={lastSync}
            lastSyncLoading={lastSyncLoading}
            availablePackages={availablePackages}
            packagesLoading={packagesLoading}
          />
          <div className="flex-container">
            <div className="cards-container">
              <DailyOverview
                selectedDate={filters.dateRange.endDate}
                selectedEnvironment={filters.selectedEnvironment}
                selectedPackage={filters.selectedPackage}
                selectedFramework={filters.selectedFramework}
                selectedUnit={filters.selectedUnit}
                data={data}
                loading={loading}
                error={error}
              />
              <EmptyCard/>
              <EmptyCard/>
              <EmptyCard/>
            </div>
          </div>
          <TestResultsTable
            selectedDate={filters.dateRange.endDate}
            selectedStartDate={filters.dateRange.startDate}
            selectedEndDate={filters.dateRange.endDate}
            datePreset={filters.datePreset}
            selectedEnvironment={filters.selectedEnvironment}
            selectedPackage={filters.selectedPackage}
            selectedFramework={filters.selectedFramework}
            selectedUnit={filters.selectedUnit}
          />
        </>
      )}
    </div>
  );
}

export default TestMonitoring;
